import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, TextField } from "@mui/material";
import { styled } from "@mui/system";
import Magnifier from "@/assets/icons/ratings/magnifier.svg";

interface AnimatedSearchInputProps {
	onInputChange: (value: string) => void;
}

const SearchWrapper = styled(Box)({
	display: "flex",
	alignItems: "center",
	position: "relative",
	top: "-5px",
});

const SearchContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	borderRadius: theme.shape.borderRadius,
	transition: "width 0.1s ease-in-out",
	width: "0px",
	overflow: "hidden",
	"&.expanded": {
		width: "150px",
	},
}));

export default function AnimatedInputSearch({ onInputChange }: AnimatedSearchInputProps) {
	const [showSearch, setShowSearch] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);

	const handleSearchClick = () => {
		setShowSearch(true);
	};

	const handleBlur = () => {
		if (inputRef.current && inputRef.current.value.trim() === "") {
			setShowSearch(false);
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
			handleBlur();
		}
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
	};

	useEffect(() => {
		const handler = setTimeout(() => {
			onInputChange(searchValue);
		}, 300);

		return () => {
			clearTimeout(handler);
		};
	}, [searchValue]);

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<SearchWrapper ref={wrapperRef}>
			<SearchContainer className={showSearch ? "expanded" : ""}>
				{showSearch && (
					<TextField
						placeholder="Search…"
						variant="standard"
						inputProps={{ "aria-label": "search" }}
						onBlur={handleBlur}
						onChange={handleInputChange}
						autoFocus
						InputProps={{
							disableUnderline: true,
							style: {
								fontFamily: "PTSansProNarrow",
								color: "var(--text-color)",
								borderBottom: "1px solid #CCCCCC",
							},
							inputRef: inputRef,
						}}
					/>
				)}
			</SearchContainer>
			<IconButton onClick={handleSearchClick}>
				<img src={Magnifier} alt="search icon" />
			</IconButton>
		</SearchWrapper>
	);
}
